import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import ClipboardListIcon from "lucide-react/dist/esm/icons/clipboard-list";
import { CandidateSkillReviewSection } from "./-components/candidate-skill-review-section";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";
import { useCandidateSkillReviewMutations } from "./-hooks/use-candidate-skill-review-mutations";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/skills/review")({
  component: SkillsReviewRouteComponent,
});

function SkillsReviewRouteComponent() {
  const { datasetSlug } = Route.useParams();
  const [page, setPage] = useState(1);
  const metaQuery = useQuery(trpc.skills.getCatalogMeta.queryOptions());
  const reviewQueueQuery = useQuery(
    trpc.skills.getCandidateSkillReviewPage.queryOptions(
      { pagination: { page, pageSize: 50 } },
      { placeholderData: (previousData) => previousData },
    ),
  );
  const meta = metaQuery.data;
  const reviewPage = reviewQueueQuery.data;
  const reviewGroups = reviewPage?.data ?? [];
  const reviewMutations = useCandidateSkillReviewMutations();

  useBreadCrumbs([
    { title: "Skills", link: `/${datasetSlug}/data/skills` },
    { title: "Review", link: `/${datasetSlug}/data/skills/review` },
  ]);

  if (metaQuery.isLoading || reviewQueueQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading review queue…</div>;
  }

  if (!meta) {
    return <div className="p-6 text-sm text-muted-foreground">Skills workspace unavailable.</div>;
  }

  return (
    <div className="mx-5 space-y-4 pb-8">
      <div className="overflow-hidden rounded-[28px] border border-amber-200/70 bg-[linear-gradient(135deg,rgba(255,247,237,0.98),rgba(255,251,235,0.94))] px-6 py-6 dark:border-amber-400/20 dark:bg-[linear-gradient(135deg,rgba(69,26,3,0.24),rgba(120,53,15,0.16))]">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-amber-500/15 p-3 text-amber-700 dark:bg-amber-300/10 dark:text-amber-300">
            <ClipboardListIcon className="size-5" />
          </div>
          <div className="max-w-3xl">
            <p className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-amber-800 dark:text-amber-300">
              Candidate Skills Review
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 dark:text-slate-50">
              Classify unrecognized candidate skills
            </h1>
            <p className="mt-2 text-sm text-amber-900/80 dark:text-amber-100/75">
              These skill terms appear on candidate profiles but haven't been matched to your catalog yet. For each
              term, link it to an existing skill, add it as new, or dismiss it.
            </p>
          </div>
        </div>
      </div>

      {reviewGroups.length === 0 ? (
        <div className="rounded-[24px] border border-black/5 bg-white/80 px-6 py-12 text-center dark:border-white/10 dark:bg-white/[0.03]">
          <p className="text-[0.68rem] uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">All Clear</p>
          <h2 className="mt-3 text-xl font-semibold text-slate-950 dark:text-slate-50">All skills are classified</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Unrecognized skill terms from candidate profiles will appear here when candidates are added or updated.
          </p>
        </div>
      ) : (
        <div className="rounded-[24px] border border-black/5 bg-white/80 dark:border-white/10 dark:bg-white/[0.03]">
          <CandidateSkillReviewSection
            datasetSlug={datasetSlug}
            skillCategories={meta.skillCategories}
            reviewGroups={reviewGroups}
            isLoading={false}
            onAliasGroup={reviewMutations.onAliasReviewGroup}
            onAliasAssignment={reviewMutations.onAliasReviewAssignment}
            onReplaceGroup={reviewMutations.onReplaceReviewGroup}
            onReplaceAssignment={reviewMutations.onReplaceReviewAssignment}
            onIgnoreGroup={reviewMutations.onIgnoreReviewGroup}
            onIgnoreAssignment={reviewMutations.onIgnoreReviewAssignment}
            onCreateRelatedGroup={reviewMutations.onCreateRelatedReviewGroup}
          />
          {reviewPage && reviewPage.total > reviewPage.pageSize && (
            <div className="flex items-center justify-between border-t border-black/5 px-5 py-3 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
              <span>
                Page {reviewPage.page} of {Math.max(reviewPage.totalPages, 1)}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-black/10 px-3 py-1.5 disabled:opacity-50 dark:border-white/10"
                  disabled={page <= 1 || reviewQueueQuery.isFetching}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-black/10 px-3 py-1.5 disabled:opacity-50 dark:border-white/10"
                  disabled={page >= reviewPage.totalPages || reviewQueueQuery.isFetching}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
